class HiyoSocialFeed extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    cancelAnimationFrame(this.initialFrame);
    this.initialFrame = null;

    this.track = this.querySelector('[data-hiyo-social-track]');
    this.querySelectorAll('[data-hiyo-social-clone]').forEach((clone) => clone.remove());
    this.items = [...this.querySelectorAll('[data-hiyo-social-item]')];
    this.previousButton = this.querySelector('[data-hiyo-social-previous]');
    this.nextButton = this.querySelector('[data-hiyo-social-next]');
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.abortController = new AbortController();
    this.activeIndex = Math.max(
      0,
      this.items.findIndex((item) => item.classList.contains('is-active')),
    );
    this.isMuted = true;
    this.isVisible = !('IntersectionObserver' in window);
    this.userPaused = false;
    this.dragState = null;
    this.ignoreScrollSelection = false;
    this.isSettling = false;
    this.motionToken = 0;
    this.visualClones = [];
    this.suppressNextClick = false;
    this.resizePending = false;

    if (!this.track || this.items.length === 0) return;

    this.createVisualClones();

    if (this.items.length < 2) {
      if (this.previousButton) this.previousButton.disabled = true;
      if (this.nextButton) this.nextButton.disabled = true;
    }

    const { signal } = this.abortController;

    this.previousButton?.addEventListener('click', () => this.goBy(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.goBy(1), { signal });
    this.track.addEventListener('scroll', this.queueScrollUpdate, { passive: true, signal });
    this.track.addEventListener('keydown', this.handleTrackKeydown, { signal });
    this.track.addEventListener('pointerdown', this.handlePointerDown, { signal });
    this.track.addEventListener('pointermove', this.handlePointerMove, { signal });
    this.track.addEventListener('pointerup', this.handlePointerUp, { signal });
    this.track.addEventListener('pointercancel', this.handlePointerUp, { signal });
    this.track.addEventListener('click', this.handleSuppressedClick, { capture: true, signal });
    this.addEventListener('click', this.handleControlClick, { signal });
    this.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });
    document.addEventListener('visibilitychange', this.handleVisibilityChange, { signal });
    this.motionQuery.addEventListener?.('change', this.handleMotionChange, { signal });

    this.items.forEach((item) => this.bindVideoEvents(item, signal));

    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.track);

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(this.handleIntersection, {
        threshold: [0, 0.35, 0.75],
      });
      this.intersectionObserver.observe(this);
    }

    this.initialFrame = requestAnimationFrame(() => {
      this.initialFrame = null;
      if (!this.isConnected) return;

      this.centerItem(this.activeIndex, 'auto');
      this.activateIndex(this.activeIndex, { force: true });
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    cancelAnimationFrame(this.initialFrame);
    this.initialFrame = null;
    cancelAnimationFrame(this.scrollFrame);
    cancelAnimationFrame(this.scrollAnimationFrame);
    clearTimeout(this.programmaticScrollTimer);
    clearTimeout(this.scrollSelectionTimer);
    clearTimeout(this.resizeTimer);
    clearTimeout(this.clickSuppressionTimer);
    this.items?.forEach((item) => this.unmountVideo(item));
  }

  get autoplayEnabled() {
    return this.dataset.autoplay !== 'false';
  }

  get reduceMotion() {
    return this.motionQuery?.matches ?? false;
  }

  get activeItem() {
    return this.items?.[this.activeIndex];
  }

  get activeVideo() {
    return this.activeItem?.querySelector('[data-hiyo-social-video]');
  }

  createVisualClones() {
    if (!this.track || this.items.length < 2) return;

    const cloneCount = Math.min(2, this.items.length - 1);
    const cloneStory = (item, index, side) => {
      const clone = item.cloneNode(true);

      clone.removeAttribute('data-hiyo-social-item');
      clone.removeAttribute('role');
      clone.removeAttribute('aria-current');
      clone.setAttribute('data-hiyo-social-clone', '');
      clone.dataset.hiyoSocialLogicalIndex = String(index);
      clone.dataset.hiyoSocialCloneSide = side;
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.classList.remove('is-active', 'is-video-ready');
      clone.querySelector('[data-hiyo-social-video]')?.remove();
      clone.querySelector('[data-hiyo-social-progress]')?.remove();
      clone.querySelector('[data-hiyo-social-video-controls]')?.remove();
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      [clone, ...clone.querySelectorAll('*')].forEach((element) => {
        [...element.attributes].forEach((attribute) => {
          if (attribute.name.startsWith('data-shopify-editor-')) {
            element.removeAttribute(attribute.name);
          }
        });
      });

      return clone;
    };

    const leadingStartIndex = this.items.length - cloneCount;
    const leadingClones = this.items.slice(-cloneCount).map((item, offset) =>
      cloneStory(item, leadingStartIndex + offset, 'leading'),
    );
    const trailingClones = this.items.slice(0, cloneCount).map((item, index) =>
      cloneStory(item, index, 'trailing'),
    );

    this.track.prepend(...leadingClones);
    this.track.append(...trailingClones);
    this.visualClones = [...leadingClones, ...trailingClones];
  }

  bindVideoEvents(item, signal) {
    const video = item.querySelector('[data-hiyo-social-video]');
    if (!video) return;

    const update = () => {
      this.updateProgress(item);
      this.updateControls(item);
    };

    video.addEventListener(
      'playing',
      () => {
        item.classList.add('is-video-ready');
        update();
      },
      { signal },
    );
    video.addEventListener('timeupdate', update, { passive: true, signal });
    video.addEventListener('durationchange', update, { passive: true, signal });
    video.addEventListener('loadedmetadata', update, { passive: true, signal });
    video.addEventListener('pause', update, { passive: true, signal });
    video.addEventListener(
      'ended',
      () => {
        update();
        if (
          item === this.activeItem &&
          this.items.length > 1 &&
          !this.dragState &&
          !this.isSettling &&
          !this.ignoreScrollSelection
        ) {
          this.goBy(1);
        }
      },
      { passive: true, signal },
    );
    video.addEventListener('volumechange', update, { passive: true, signal });
    video.addEventListener('error', update, { passive: true, signal });
  }

  queueScrollUpdate = () => {
    if (this.ignoreScrollSelection || this.dragState || this.isSettling) return;

    clearTimeout(this.scrollSelectionTimer);
    this.scrollSelectionTimer = setTimeout(() => {
      if (this.ignoreScrollSelection || this.dragState || this.isSettling) return;

      const candidate = this.getNearestCandidate();
      if (!candidate) return;

      this.selectIndex(candidate.index, {
        targetElement: candidate.element,
      });
    }, 96);
  };

  getPhysicalItems() {
    if (!this.track) return [];

    return [
      ...this.track.querySelectorAll(
        '[data-hiyo-social-item], [data-hiyo-social-clone]',
      ),
    ];
  }

  getLogicalIndex(item) {
    if (!item) return -1;

    if (item.hasAttribute('data-hiyo-social-clone')) {
      return Number.parseInt(item.dataset.hiyoSocialLogicalIndex, 10);
    }

    return this.items.indexOf(item);
  }

  getClone(index, side) {
    return this.visualClones.find(
      (clone) =>
        Number.parseInt(clone.dataset.hiyoSocialLogicalIndex, 10) === index &&
        clone.dataset.hiyoSocialCloneSide === side,
    );
  }

  getNearestCandidate() {
    if (!this.track || this.items.length === 0) return null;

    const trackBounds = this.track.getBoundingClientRect();
    const trackCenter = trackBounds.left + trackBounds.width / 2;
    let nearestElement = null;
    let nearestDistance = Number.POSITIVE_INFINITY;

    this.getPhysicalItems().forEach((item) => {
      const bounds = item.getBoundingClientRect();
      const distance = Math.abs(bounds.left + bounds.width / 2 - trackCenter);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestElement = item;
      }
    });

    if (!nearestElement) return null;

    return {
      element: nearestElement,
      index: this.getLogicalIndex(nearestElement),
      isClone: nearestElement.hasAttribute('data-hiyo-social-clone'),
      side: nearestElement.dataset.hiyoSocialCloneSide || null,
      distance: nearestDistance,
    };
  }

  getNearestIndex() {
    return this.getNearestCandidate()?.index ?? 0;
  }

  getItemTargetLeft(item) {
    if (!this.track || !item) return;

    const trackBounds = this.track.getBoundingClientRect();
    const itemBounds = item.getBoundingClientRect();
    const centerDelta =
      itemBounds.left + itemBounds.width / 2 - (trackBounds.left + trackBounds.width / 2);
    const targetLeft = this.track.scrollLeft + centerDelta;

    return Math.min(
      Math.max(0, targetLeft),
      Math.max(0, this.track.scrollWidth - this.track.clientWidth),
    );
  }

  centerItem(index, behavior = 'auto', targetElement = null) {
    const item = targetElement || this.items[index];
    const targetLeft = this.getItemTargetLeft(item);
    if (!this.track || targetLeft === undefined) return;

    this.track.scrollTo({ left: targetLeft, behavior });
  }

  cancelScrollAnimation() {
    this.motionToken += 1;
    cancelAnimationFrame(this.scrollAnimationFrame);
    this.scrollAnimationFrame = null;
    this.isSettling = false;
    this.track?.classList.remove('is-settling');
    this.animationTarget?.classList.remove('is-target', 'is-wrap-target');
    this.animationTarget = null;
  }

  animateScrollTo(targetLeft, { initialVelocity = 0, onComplete } = {}) {
    if (!this.track) return;

    const token = this.motionToken;
    const startTime = performance.now();
    let lastTime = startTime;
    let position = this.track.scrollLeft;
    let velocity = Math.max(-38, Math.min(38, initialVelocity));

    if (this.reduceMotion || Math.abs(targetLeft - position) < 0.5) {
      this.track.scrollLeft = targetLeft;
      onComplete?.();
      return;
    }

    this.isSettling = true;
    this.track.classList.add('is-settling');

    const tick = (now) => {
      if (token !== this.motionToken || !this.track) return;

      const frameScale = Math.max(
        0.2,
        Math.min(3, (now - lastTime) / (1000 / 60)),
      );
      lastTime = now;

      velocity += (targetLeft - position) * 0.075 * frameScale;
      velocity *= Math.pow(0.65, frameScale);
      position += velocity * frameScale;

      this.track.scrollLeft = position;

      const remaining = targetLeft - position;
      const elapsed = now - startTime;
      if (
        (Math.abs(remaining) < 0.85 && Math.abs(velocity) < 0.55) ||
        elapsed >= 460
      ) {
        this.track.scrollLeft = targetLeft;
        this.isSettling = false;
        this.track.classList.remove('is-settling');
        this.scrollAnimationFrame = null;
        onComplete?.();
        return;
      }

      this.scrollAnimationFrame = requestAnimationFrame(tick);
    };

    this.scrollAnimationFrame = requestAnimationFrame(tick);
  }

  selectIndex(
    index,
    {
      targetElement = null,
      initialVelocity = 0,
      behavior = this.reduceMotion ? 'auto' : 'spring',
    } = {},
  ) {
    if (this.items.length === 0) return;

    const normalizedIndex = ((index % this.items.length) + this.items.length) % this.items.length;
    let resolvedTarget = targetElement;

    if (!resolvedTarget && index < 0 && normalizedIndex === this.items.length - 1) {
      resolvedTarget = this.getClone(normalizedIndex, 'leading');
    } else if (!resolvedTarget && index >= this.items.length && normalizedIndex === 0) {
      resolvedTarget = this.getClone(normalizedIndex, 'trailing');
    }

    resolvedTarget ||= this.items[normalizedIndex];
    if (!resolvedTarget) return;

    this.cancelScrollAnimation();
    this.ignoreScrollSelection = true;
    clearTimeout(this.scrollSelectionTimer);
    this.animationTarget = resolvedTarget;
    resolvedTarget.classList.add('is-target');

    const isClone = resolvedTarget.hasAttribute('data-hiyo-social-clone');
    if (isClone) resolvedTarget.classList.add('is-wrap-target');

    const completeSelection = () => {
      if (isClone) {
        this.centerItem(normalizedIndex, 'auto');
      }

      resolvedTarget.classList.remove('is-target', 'is-wrap-target');
      this.animationTarget = null;
      this.activateIndex(normalizedIndex);

      requestAnimationFrame(() => {
        this.ignoreScrollSelection = false;
      });
    };

    const targetLeft = this.getItemTargetLeft(resolvedTarget);
    if (targetLeft === undefined) {
      completeSelection();
      return;
    }

    if (behavior === 'auto') {
      this.track.scrollLeft = targetLeft;
      completeSelection();
      return;
    }

    this.animateScrollTo(targetLeft, {
      initialVelocity,
      onComplete: completeSelection,
    });
  }

  activateIndex(index, { force = false } = {}) {
    const nextItem = this.items[index];
    if (!nextItem || (!force && index === this.activeIndex)) return;

    const previousItem = this.activeItem;
    if (previousItem && previousItem !== nextItem) {
      this.unmountVideo(previousItem);
    }

    this.activeIndex = index;
    this.userPaused = false;

    this.items.forEach((item, itemIndex) => {
      const isActive = itemIndex === index;
      const controls = item.querySelector('[data-hiyo-social-video-controls]');
      const progress = item.querySelector('[data-hiyo-social-progress]');

      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-current', isActive ? 'true' : 'false');
      if (controls) controls.hidden = !isActive;
      if (progress) progress.hidden = !isActive;

      if (!isActive) {
        this.updateProgress(item);
        this.updateControls(item);
      }
    });

    const video = nextItem.querySelector('[data-hiyo-social-video]');
    if (video) {
      this.mountVideo(nextItem);
      video.muted = this.isMuted;
      this.playVideo(video, { automatic: true });
    }

    this.updateProgress(nextItem);
    this.updateControls(nextItem);
  }

  goBy(direction) {
    this.selectIndex(this.activeIndex + direction);
  }

  handleTrackKeydown = (event) => {
    if (event.target !== this.track) return;

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.goBy(event.key === 'ArrowLeft' ? -1 : 1);
      return;
    }

    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      this.selectIndex(event.key === 'Home' ? 0 : this.items.length - 1);
    }
  };

  handlePointerDown = (event) => {
    if (
      !event.isPrimary ||
      (event.pointerType === 'mouse' && event.button !== 0) ||
      event.target.closest('button')
    ) {
      return;
    }

    this.cancelScrollAnimation();
    clearTimeout(this.scrollSelectionTimer);
    this.ignoreScrollSelection = true;
    this.dragState = {
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastTime: performance.now(),
      startScrollLeft: this.track.scrollLeft,
      startIndex: this.activeIndex,
      distanceX: 0,
      distanceY: 0,
      velocityX: 0,
      axis: null,
    };

    if (event.pointerType === 'mouse') {
      this.track.setPointerCapture(event.pointerId);
    }
  };

  handlePointerMove = (event) => {
    if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

    const distanceX = event.clientX - this.dragState.startX;
    const distanceY = event.clientY - this.dragState.startY;
    this.dragState.distanceX = distanceX;
    this.dragState.distanceY = distanceY;

    if (!this.dragState.axis && Math.max(Math.abs(distanceX), Math.abs(distanceY)) >= 6) {
      this.dragState.axis =
        Math.abs(distanceX) > Math.abs(distanceY) * 1.08 ? 'horizontal' : 'vertical';

      if (this.dragState.axis === 'horizontal') {
        this.track.classList.add('is-dragging');
        if (!this.track.hasPointerCapture(event.pointerId)) {
          this.track.setPointerCapture(event.pointerId);
        }
      }
    }

    if (this.dragState.axis !== 'horizontal') return;

    const now = performance.now();
    const elapsed = Math.max(1, now - this.dragState.lastTime);
    const instantaneousVelocity = (event.clientX - this.dragState.lastX) / elapsed;
    this.dragState.velocityX =
      this.dragState.velocityX * 0.55 + instantaneousVelocity * 0.45;
    this.dragState.lastX = event.clientX;
    this.dragState.lastTime = now;

    this.track.scrollLeft = this.dragState.startScrollLeft - distanceX;
    if (event.cancelable) event.preventDefault();
  };

  handlePointerUp = (event) => {
    if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

    const dragState = this.dragState;
    const wasHorizontal = dragState.axis === 'horizontal';
    const moved = wasHorizontal && Math.abs(dragState.distanceX) > 4;
    this.dragState = null;
    this.track.classList.remove('is-dragging');

    if (this.track.hasPointerCapture(event.pointerId)) {
      this.track.releasePointerCapture(event.pointerId);
    }

    if (!wasHorizontal || event.type === 'pointercancel') {
      this.suppressNextClick = false;
      clearTimeout(this.clickSuppressionTimer);
      this.ignoreScrollSelection = false;
      if (event.type === 'pointercancel' && wasHorizontal) {
        this.resizePending = false;
        this.selectIndex(dragState.startIndex);
      } else if (this.resizePending) {
        this.resizePending = false;
        this.centerItem(this.activeIndex, 'auto');
      }
      return;
    }

    this.resizePending = false;
    this.suppressNextClick = moved;
    clearTimeout(this.clickSuppressionTimer);
    if (moved) {
      this.clickSuppressionTimer = setTimeout(() => {
        this.suppressNextClick = false;
      }, 420);
    }

    const nearest = this.getNearestCandidate();
    const cardWidth = this.items[dragState.startIndex]?.getBoundingClientRect().width || 258;
    const travelThreshold = Math.min(64, cardWidth * 0.2);
    const velocityThreshold = 0.32;
    const releaseAge = performance.now() - dragState.lastTime;
    const releaseVelocity = releaseAge <= 96 ? dragState.velocityX : 0;
    const direction =
      Math.abs(dragState.distanceX) >= 6
        ? dragState.distanceX < 0
          ? 1
          : -1
        : releaseVelocity < 0
          ? 1
          : -1;
    const shouldAdvance =
      Math.abs(dragState.distanceX) >= travelThreshold ||
      Math.abs(releaseVelocity) >= velocityThreshold;

    let targetIndex = nearest?.index ?? dragState.startIndex;
    let targetElement = nearest?.element || this.items[targetIndex];

    if (targetIndex === dragState.startIndex && shouldAdvance) {
      const rawTargetIndex = dragState.startIndex + direction;
      targetIndex =
        ((rawTargetIndex % this.items.length) + this.items.length) % this.items.length;

      if (rawTargetIndex < 0) {
        targetElement = this.getClone(targetIndex, 'leading');
      } else if (rawTargetIndex >= this.items.length) {
        targetElement = this.getClone(targetIndex, 'trailing');
      } else {
        targetElement = this.items[targetIndex];
      }
    }

    this.selectIndex(targetIndex, {
      targetElement,
      initialVelocity: -releaseVelocity * (1000 / 60),
    });
  };

  handleSuppressedClick = (event) => {
    if (!this.suppressNextClick) return;

    this.suppressNextClick = false;
    clearTimeout(this.clickSuppressionTimer);
    event.preventDefault();
    event.stopPropagation();
  };

  handleControlClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const control = event.target.closest(
      '[data-hiyo-social-replay], [data-hiyo-social-pause], [data-hiyo-social-play], [data-hiyo-social-mute], [data-hiyo-social-unmute]',
    );
    if (!control) return;

    const item = control.closest('[data-hiyo-social-item]');
    const video = item?.querySelector('[data-hiyo-social-video]');
    if (!video || item !== this.activeItem) return;

    if (control.hasAttribute('data-hiyo-social-replay')) {
      try {
        video.currentTime = 0;
      } catch {
        // A not-yet-loaded remote video will start from zero naturally.
      }
      this.userPaused = false;
      this.playVideo(video, { automatic: false });
    } else if (control.hasAttribute('data-hiyo-social-pause')) {
      this.userPaused = true;
      video.pause();
    } else if (control.hasAttribute('data-hiyo-social-play')) {
      this.userPaused = false;
      this.playVideo(video, { automatic: false });
    } else if (control.hasAttribute('data-hiyo-social-mute')) {
      this.isMuted = true;
      video.muted = true;
    } else if (control.hasAttribute('data-hiyo-social-unmute')) {
      this.isMuted = false;
      video.muted = false;
    }

    this.updateControls(item);
  };

  handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('[data-hiyo-social-item]');
    const index = this.items.indexOf(item);
    if (index < 0) return;

    this.selectIndex(index);
  };

  handleResize = () => {
    clearTimeout(this.resizeTimer);
    clearTimeout(this.scrollSelectionTimer);

    if (this.dragState) {
      this.resizePending = true;
      return;
    }

    this.resizeTimer = setTimeout(() => {
      if (this.dragState) {
        this.resizePending = true;
        return;
      }

      this.resizePending = false;
      const target = this.animationTarget;
      const targetIndex = this.getLogicalIndex(target);
      if (target && targetIndex >= 0) {
        this.selectIndex(targetIndex, { behavior: 'auto' });
        return;
      }

      this.cancelScrollAnimation();
      this.ignoreScrollSelection = true;
      this.centerItem(this.activeIndex, 'auto');
      requestAnimationFrame(() => {
        this.ignoreScrollSelection = false;
      });
    }, 80);
  };

  handleIntersection = (entries) => {
    const entry = entries[entries.length - 1];
    const wasVisible = this.isVisible;
    this.isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.35;

    if (this.isVisible && !wasVisible) {
      this.playVideo(this.activeVideo, { automatic: true });
    } else if (!this.isVisible && wasVisible) {
      this.activeVideo?.pause();
    }
  };

  handleVisibilityChange = () => {
    if (document.hidden) {
      this.activeVideo?.pause();
    } else {
      this.playVideo(this.activeVideo, { automatic: true });
    }
  };

  handleMotionChange = () => {
    if (this.reduceMotion && this.animationTarget) {
      const target = this.animationTarget;
      const targetIndex = this.getLogicalIndex(target);
      if (targetIndex >= 0) {
        this.selectIndex(targetIndex, {
          targetElement: target,
          behavior: 'auto',
        });
      }
    }

    if (this.reduceMotion) {
      this.activeVideo?.pause();
    } else {
      this.playVideo(this.activeVideo, { automatic: true });
    }
    this.updateControls(this.activeItem);
  };

  playVideo(video, { automatic }) {
    if (!video) return;

    const ownerItem = video.closest('[data-hiyo-social-item]');

    if (
      automatic &&
      (!this.autoplayEnabled ||
        this.reduceMotion ||
        !this.isVisible ||
        document.hidden ||
        this.userPaused)
    ) {
      this.updateControls(this.activeItem);
      return;
    }

    video.muted = this.isMuted;
    const playPromise = video.play();

    if (playPromise) {
      playPromise
        .then(() => {
          if (video !== this.activeVideo || ownerItem !== this.activeItem) return;

          ownerItem?.classList.add('is-video-ready');
          this.updateControls(ownerItem);
        })
        .catch(() => {
          if (video === this.activeVideo && ownerItem === this.activeItem) {
            this.updateControls(ownerItem);
          }
        });
    }
  }

  mountVideo(item) {
    const video = item?.querySelector('[data-hiyo-social-video]');
    if (!video || video.dataset.hiyoSocialMounted === 'true') return;

    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
    });
    video.dataset.hiyoSocialMounted = 'true';
    video.preload = 'metadata';
    video.load();
  }

  unmountVideo(item) {
    const video = item?.querySelector('[data-hiyo-social-video]');
    if (!video) return;

    video.pause();
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.removeAttribute('src');
    });
    video.removeAttribute('src');
    delete video.dataset.hiyoSocialMounted;
    video.preload = 'none';
    video.load();
    item.classList.remove('is-video-ready');
    this.updateProgress(item);
    this.updateControls(item);
  }

  updateProgress(item) {
    if (!item) return;

    const video = item.querySelector('[data-hiyo-social-video]');
    const progress = item.querySelector('[data-hiyo-social-progress]');
    const fill = item.querySelector('[data-hiyo-social-progress-fill]');
    if (!video || !progress || !fill) return;

    const metadataDuration =
      Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
    const fallbackDuration = Number.parseFloat(item.dataset.duration) || 1;
    const duration = metadataDuration || fallbackDuration;
    const ratio = video.ended
      ? 1
      : Math.min(1, Math.max(0, (Number.isFinite(video.currentTime) ? video.currentTime : 0) / duration));
    const percent = Math.round(ratio * 100);

    fill.style.transform = `scaleX(${ratio})`;
    progress.setAttribute('aria-valuenow', String(percent));
  }

  updateControls(item) {
    if (!item) return;

    const video = item.querySelector('[data-hiyo-social-video]');
    const controls = item.querySelector('[data-hiyo-social-video-controls]');
    if (!video || !controls) return;

    const replayButton = controls.querySelector('[data-hiyo-social-replay]');
    const pauseButton = controls.querySelector('[data-hiyo-social-pause]');
    const playButton = controls.querySelector('[data-hiyo-social-play]');
    const muteButton = controls.querySelector('[data-hiyo-social-mute]');
    const unmuteButton = controls.querySelector('[data-hiyo-social-unmute]');
    const ended = video.ended;
    const paused = video.paused;

    if (replayButton) replayButton.hidden = !ended;
    if (pauseButton) pauseButton.hidden = paused || ended;
    if (playButton) playButton.hidden = !paused || ended;
    if (muteButton) muteButton.hidden = video.muted;
    if (unmuteButton) unmuteButton.hidden = !video.muted;
  }
}

if (!customElements.get('hiyo-social-feed')) {
  customElements.define('hiyo-social-feed', HiyoSocialFeed);
}
